import * as React from 'react';
import {Select, type SelectMultiProps, type SingleSelectProps} from '../select';
import {defaultFilterOption, type SelectValue} from '../_select-internals';
import type {SelectOption} from '../../lib/selector-option';
import type {ReactNode} from 'react';
import {VirtualMenuList} from './VirtualMenuList';
import {
  InfiniteScrollContext,
  type InfiniteScrollContextValue,
} from './InfiniteScrollContext';

type InfiniteScrollExtras = {
  canLoadMore?: boolean;
  isLoadingMore?: boolean;
  loadMoreItems?: () => void;
  loadingMoreText?: string;
  onSearchChange?: (input: string) => void;
  itemHeight?: number;
  listHeight?: number;
  overscan?: number;
  loadMoreThreshold?: number;
};

type InfiniteScrollSingleProps<T, V extends SelectValue, L extends ReactNode> = Omit<
  SingleSelectProps<T, V, L>,
  'options'
> &
  InfiniteScrollExtras & {
    options?: SelectOption<T, V, L>[];
    getFullSearchOption?: (query: string) => SelectOption<T, V, L> | null | undefined;
  };

type InfiniteScrollMultiSelectProps<T, V extends SelectValue, L extends ReactNode> = Omit<
  SelectMultiProps<T, V, L>,
  'options'
> &
  InfiniteScrollExtras & {
    options?: SelectOption<T, V, L>[];
  };

type InfiniteScrollSelectProps<
  T = unknown,
  V extends SelectValue = string,
  L extends ReactNode = string,
> = InfiniteScrollSingleProps<T, V, L> | InfiniteScrollMultiSelectProps<T, V, L>;

const DEFAULT_ITEM_HEIGHT = 60;
const DEFAULT_LIST_HEIGHT = 322;
const DEFAULT_OVERSCAN = 5;
const DEFAULT_LOAD_MORE_THRESHOLD = 5;

const passthroughFilter = () => true;

function InfiniteScrollSelectInternal<
  T = unknown,
  V extends SelectValue = string,
  L extends ReactNode = string,
>(props: InfiniteScrollSelectProps<T, V, L>, ref: React.Ref<HTMLDivElement>) {
  const {
    options: rawOptions = [],
    canLoadMore,
    isLoadingMore,
    loadMoreItems,
    loadingMoreText,
    onSearchChange,
    itemHeight = DEFAULT_ITEM_HEIGHT,
    listHeight = DEFAULT_LIST_HEIGHT,
    overscan = DEFAULT_OVERSCAN,
    loadMoreThreshold = DEFAULT_LOAD_MORE_THRESHOLD,
    getFullSearchOption: getFullSearchOptionProp,
    filterOption: userFilterOption,
    components: userComponents,
    onInputChange: userOnInputChange,
    isMulti = false,
    ...rest
  } = props as InfiniteScrollSingleProps<T, V, L>;

  const getFullSearchOption = isMulti ? undefined : getFullSearchOptionProp;

  const isPaginated =
    canLoadMore !== undefined ||
    isLoadingMore !== undefined ||
    loadMoreItems !== undefined ||
    onSearchChange !== undefined ||
    getFullSearchOption !== undefined;

  const filterOption =
    userFilterOption ?? (isPaginated ? passthroughFilter : defaultFilterOption);

  const [inputValue, setInputValue] = React.useState('');
  const selectedResetLabelRef = React.useRef('');
  const valueLabel = React.useMemo(() => {
    if (isMulti) return '';
    const single = (rest as InfiniteScrollSingleProps<T, V, L>).value;
    if (!single) return '';
    return rest.getValueLabel?.(single) ?? String(single.label);
  }, [isMulti, rest]);

  const filteredOptions = React.useMemo(() => {
    const trimmed = inputValue.trim();
    const isFiltering =
      trimmed.length > 0 && trimmed.toLowerCase() !== valueLabel.toLowerCase();
    let list = rawOptions;
    if (isFiltering) {
      list = list.filter(option => filterOption(option, trimmed));
    }
    if (getFullSearchOption && trimmed) {
      const synthetic = getFullSearchOption(trimmed);
      if (synthetic) list = [synthetic, ...list];
    }
    return list;
  }, [rawOptions, inputValue, filterOption, getFullSearchOption, valueLabel]);

  const contextValue = React.useMemo<InfiniteScrollContextValue>(
    () => ({
      canLoadMore,
      isLoadingMore,
      loadMoreItems,
      loadingMoreText,
      itemHeight,
      listHeight,
      overscan,
      loadMoreThreshold,
    }),
    [
      canLoadMore,
      isLoadingMore,
      loadMoreItems,
      loadingMoreText,
      itemHeight,
      listHeight,
      overscan,
      loadMoreThreshold,
    ],
  );

  const components = React.useMemo(
    () => ({...userComponents, MenuList: VirtualMenuList}),
    [userComponents],
  );

  const handleInputChange = React.useCallback(
    (value: string) => {
      const selectedResetLabel = selectedResetLabelRef.current;
      const isSelectedLabelReset =
        !isMulti &&
        value.trim().toLowerCase() === (selectedResetLabel || valueLabel).toLowerCase();
      const searchValue = isSelectedLabelReset ? '' : value;

      setInputValue(value);
      onSearchChange?.(searchValue);
      userOnInputChange?.(value);
      if (!isSelectedLabelReset) selectedResetLabelRef.current = '';
    },
    [isMulti, onSearchChange, userOnInputChange, valueLabel],
  );

  const selectExtras = {
    options: filteredOptions,
    filterOption: passthroughFilter,
    components,
    onInputChange: handleInputChange,
  };

  return (
    <InfiniteScrollContext.Provider value={contextValue}>
      {isMulti ? (
        <Select
          ref={ref}
          {...(rest as unknown as SelectMultiProps<T, V, L>)}
          {...selectExtras}
          onChange={(next, meta) => {
            selectedResetLabelRef.current = '';
            onSearchChange?.('');
            (rest as unknown as SelectMultiProps<T, V, L>).onChange?.(next, meta);
          }}
          isMulti
        />
      ) : (
        <Select
          ref={ref}
          {...(rest as unknown as SingleSelectProps<T, V, L>)}
          {...selectExtras}
          onChange={(next, meta) => {
            selectedResetLabelRef.current = next
              ? ((rest as InfiniteScrollSingleProps<T, V, L>).getValueLabel?.(next) ??
                String(next.label))
              : '';
            onSearchChange?.('');
            (rest as unknown as SingleSelectProps<T, V, L>).onChange?.(next, meta);
          }}
          isMulti={false}
        />
      )}
    </InfiniteScrollContext.Provider>
  );
}

type InfiniteScrollSelectComponent = <
  T = unknown,
  V extends SelectValue = string,
  L extends ReactNode = string,
>(
  props: InfiniteScrollSelectProps<T, V, L> & {ref?: React.Ref<HTMLDivElement>},
) => React.ReactElement;

const InfiniteScrollSelect = React.forwardRef(
  InfiniteScrollSelectInternal,
) as unknown as InfiniteScrollSelectComponent;

export {InfiniteScrollSelect};
export type {InfiniteScrollSelectProps};
